import { useCallback, useState } from "react"

import {
  AiEngineRoutingOverview,
  compareEngineVersion,
  disableAiEngine,
  enableAiEngine,
  getAiEngineRouting,
  pinAiEngine,
  rollbackAiEngineRouting,
  setAiEngineDefault,
  unpinAiEngine
} from "@domain/ai_engine"
import useTranslate from "@hooks/use_translate"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"

import CapabilitySection from "./sub_components/CapabilitySection"
import HistorySection from "./sub_components/HistorySection"
import HowItWorks from "./sub_components/HowItWorks"
import PinsSection from "./sub_components/PinsSection"
import ReasonDialog, {
  ReasonDialogRequest
} from "./sub_components/ReasonDialog"

const QUERY_KEY = ["ai-engine-routing"]

type OverviewCall = () => Promise<{
  isSuccess: boolean
  data?: AiEngineRoutingOverview
  errorMessage?: string
}>

/**
 * AI Hub → Engine Routing. Which engine version answers which customer, changed here instead
 * of over the CLI: same routing document, same history, same instant effect on every replica.
 */
export default function AiEnginePage() {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const [request, setRequest] = useState<ReasonDialogRequest | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await getAiEngineRouting()
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      const overview: AiEngineRoutingOverview = response.data
      return overview
    }
  })

  /** Every write returns the fresh overview; it replaces the cached one so the page never shows a stale state. */
  const apply = useCallback(
    async (call: OverviewCall) => {
      const response = await call()
      if (!response.isSuccess || !response.data) {
        showErrorToast(
          response.errorMessage || translate("ai_engine_change_failed")
        )
        throw new Error(response.errorMessage || "failed")
      }
      queryClient.setQueryData(QUERY_KEY, response.data)
      showSuccessToast(translate("ai_engine_change_applied"))
    },
    [queryClient, translate]
  )

  const actions = {
    setDefault: (capability: string, version: string, reason: string) =>
      apply(() => setAiEngineDefault({ capability, version, reason })),
    disable: (capability: string, version: string, reason: string) =>
      apply(() => disableAiEngine({ capability, version, reason })),
    enable: (capability: string, version: string, reason: string) =>
      apply(() => enableAiEngine({ capability, version, reason })),
    pin: (
      pin: {
        customerId: string
        version: string
        capability?: string
        note?: string
      },
      reason: string
    ) => apply(() => pinAiEngine({ ...pin, reason })),
    unpin: (
      customerId: string,
      capability: string | undefined,
      reason: string
    ) => apply(() => unpinAiEngine({ customerId, capability, reason })),
    rollback: (historyIndex: number, reason: string) =>
      apply(() => rollbackAiEngineRouting({ historyIndex, reason }))
  }

  if (isLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Box p={3}>
        <Alert severity="error">{translate("ai_engine_load_failed")}</Alert>
      </Box>
    )
  }

  const engines = [...data.engines].sort((a, b) =>
    compareEngineVersion(a.version, b.version)
  )

  return (
    <Stack gap={2} p={3} maxWidth={1100}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <Stack gap={0.5}>
          <Typography variant="h5" fontWeight={600}>
            {translate("ai_engine_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_subtitle")}
          </Typography>
        </Stack>
        <Tooltip title={translate("ai_engine_refresh")}>
          <IconButton onClick={() => void refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <HowItWorks />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {translate("ai_engine_engines_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate("ai_engine_engines_desc")}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {engines.map((engine) => (
              <Chip
                key={engine.version}
                label={`${engine.version} · ${engine.capabilities.join(", ")}${
                  engine.frozen ? ` · ${translate("ai_engine_frozen")}` : ""
                }`}
                variant="outlined"
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {translate("ai_engine_last_change")
              .replace("{by}", data.routing.updatedBy)
              .replace(
                "{at}",
                dayjs(data.routing.updatedAt).format("DD.MM.YYYY HH:mm")
              )}
          </Typography>
        </Stack>
      </Paper>

      <CapabilitySection overview={data} ask={setRequest} actions={actions} />
      <PinsSection overview={data} ask={setRequest} actions={actions} />
      <HistorySection
        history={data.routing.history}
        ask={setRequest}
        rollback={actions.rollback}
      />

      <ReasonDialog request={request} onClose={() => setRequest(null)} />
    </Stack>
  )
}
